import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, Send, X, Loader, MessageSquare, Zap, Edit } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from "../assets/icon.png";

import { RealtimeClient } from '@openai/realtime-api-beta';
import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { instructions } from '../utils/conversation_config.js';

const LOCAL_RELAY_SERVER_URL = import.meta.env.VITE_LOCAL_RELAY_SERVER_URL;
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

const MobileChatInterface = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState('voice');
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioTranscript, setAudioTranscript] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [visualizerValues, setVisualizerValues] = useState(Array(8).fill(5));
  const [isConnected, setIsConnected] = useState(false);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [currentResponseId, setCurrentResponseId] = useState(null);

  const messagesEndRef = useRef(null);
  const clientRef = useRef(null);
  const wavRecorderRef = useRef(new WavRecorder({ sampleRate: 24000 }));
  const wavStreamPlayerRef = useRef(new WavStreamPlayer({ sampleRate: 24000 }));
  const processingTimeoutRef = useRef(null);
  const responseContentRef = useRef(''); // Track accumulated response content
  const longPressTimerRef = useRef(null); // Reference for long press timer
  const isLongPressingRef = useRef(false); // Track if currently in long press

  // Auto-scroll on messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Visualizer while recording
  useEffect(() => {
    if (isRecording) {
      const interval = setInterval(() => {
        const recorder = wavRecorderRef.current;
        if (recorder.recording && recorder.getFrequencies) {
          try {
            const result = recorder.getFrequencies('voice');
            const values = Array(8)
              .fill()
              .map((_, i) => {
                const idx = Math.floor((i / 8) * result.values.length);
                return 5 + Math.floor(result.values[idx] * 15);
              });
            setVisualizerValues(values);
          } catch {
            setVisualizerValues(Array(8).fill().map(() => Math.floor(Math.random() * 15) + 5));
          }
        }
      }, 80);
      return () => clearInterval(interval);
    } else {
      setVisualizerValues(Array(8).fill(5));
    }
  }, [isRecording]);

  useEffect(() => {
    const interval = setInterval(() => {
      const player = wavStreamPlayerRef.current;
      if (player.analyser) {
        const freqs = player.getFrequencies('voice').values;
        const maxAmp = Math.max(...freqs);
        setIsAgentSpeaking(maxAmp > 0.001);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);
  
  // Initialize RealtimeClient and event handlers
  useEffect(() => {
    clientRef.current = new RealtimeClient({
      url: LOCAL_RELAY_SERVER_URL
    });
    
    const client = clientRef.current;
    client.updateSession({ instructions });
    client.updateSession({ input_audio_transcription: { model: 'whisper-1' } });

    client.on('realtime.event', async (evt) => {
      if (evt.event.type === 'conversation.item.input_audio_transcription.completed') {
        const transcript = evt.event.transcript;
        setAudioTranscript(transcript);
        setMessages(prev => [...prev, { type: 'user', content: transcript, time: new Date() }]);
        await injectContext(transcript);
        setIsLoading(true);
      }
    });

    client.on('conversation.interrupted', async () => {
      const to = await wavStreamPlayerRef.current.interrupt();
      if (to?.trackId) await client.cancelResponse(to.trackId, to.offset);
      // Add a partial message entry if we have accumulated content
      if (responseContentRef.current) {
        setMessages(prev => [
          ...prev, 
          { 
            type: 'bot', 
            content: responseContentRef.current + " (interrupted)", 
            time: new Date(),
            interrupted: true
          }
        ]);
        responseContentRef.current = ''; // Reset accumulated content
      }
      setCurrentResponseId(null);
    });

    client.on('conversation.updated', async ({ item, delta }) => {
      if (mode === 'voice' && delta?.audio) {
        // Track the current response ID
        setCurrentResponseId(item.id);
        
        // In voice mode, stop user recording, start agent speaking
        setIsRecording(false);
        setIsAgentSpeaking(true);
        wavStreamPlayerRef.current.add16BitPCM(delta.audio, item.id);
    
        // Clear the processing timeout if any
        if (processingTimeoutRef.current) clearTimeout(processingTimeoutRef.current);
        
        // Accumulate text content for potential interrupted messages
        if (delta.text) {
          responseContentRef.current += delta.text;
        }
      }
    
      if (item.role === 'assistant' && item.status === 'completed') {
        setIsLoading(false);  // Stop loading when the assistant response is received
        setCurrentResponseId(null);
    
        setMessages(prev => [
          ...prev,
          { type: 'bot', content: item.formatted.text || '', time: new Date() }
        ]);
        
        // Reset accumulated content
        responseContentRef.current = '';
    
        // For voice mode, stop agent speaking after a small delay
        if (mode === 'voice') {
          processingTimeoutRef.current = setTimeout(() => {
            setIsAgentSpeaking(false); // Stop agent speaking after a short delay
          }, 300);
        }
      }
    });
    
    wavStreamPlayerRef.current.onPlaybackEnd = () => {
      setIsAgentSpeaking(false);
      setIsLoading(false);
    };

    return () => {
      clientRef.current.reset();
      if (processingTimeoutRef.current) clearTimeout(processingTimeoutRef.current);
    };
  }, []);

  const injectContext = async (transcript, isChatMode) => {
    const client = clientRef.current;
    if (!client?.isConnected()) return;
    transcript = transcript.trim();
    if (!transcript) return;
  
    try {
      // Set isLoading to true when waiting for the backend response
      setIsLoading(true);
  
      // Send to backend for text response if it's chat mode
      const url = `${BACKEND_URL}/context?query=${encodeURIComponent(transcript)}&isChatMode=${isChatMode}`;

      // Make the backend request to get the response
      const resp = await fetch(url);
      const data = await resp.json();
  
      // If it's chat mode, display the response in the chat
      if (isChatMode) {
        setMessages(prev => [
          ...prev,
          { type: 'bot', content: data.message, time: new Date() }
        ]);
        setIsLoading(false);  
       
      } else {
        // Handle other cases (e.g., voice mode)
        client.sendUserMessageContent([{ type: 'input_text', text: data.message }]);
        if (!client.getTurnDetectionType()) client.createResponse();
      }
  
      // Optionally handle the image URL (if returned)
      if (data.imageUrl) {
        setImageUrl(data.imageUrl);
      }
    } catch (error) {
      console.error('Error fetching response:', error);
      setIsLoading(false);
    }
  };
  
  const connectConversation = async () => {
    const client = clientRef.current;
  
    // If in voice mode, initialize the audio recorder and player
    if (mode === 'voice') {
      await wavRecorderRef.current.begin();
      await wavStreamPlayerRef.current.connect();
      await client.connect();
      client.sendUserMessageContent([{ type: 'input_text', text: 'Hello!' }]);
      setIsConnected(true);
      setIsLoading(false);
  
      if (client.getTurnDetectionType() === 'server_vad') {
        wavRecorderRef.current.record(data => client.appendInputAudio(data.mono));
      }
    } else {
      // For chat mode, just establish the connection without audio
      await client.connect();
      setIsConnected(true);
      setIsLoading(false);
  
      // Send a greeting message in chat mode
      setMessages(prev => [
        ...prev,
        { type: 'bot', content: "Hi there, how can I help you today?", time: new Date() }
      ]);
    }
  };
  
  /**
   * Disconnect and reset conversation state
   */
  const disconnectConversation = useCallback(async () => {
    // 1) clear all UI flags
    setIsRecording(false);
    setIsAgentSpeaking(false);
    setIsConnected(false);
    setIsLoading(false);
    setCurrentResponseId(null);
  
    // 2) clear chat state
    setMessages([]);
    setInputText('');
    setAudioTranscript('');
    responseContentRef.current = '';
  
    // 3) disconnect client
    const client = clientRef.current;
    if (client) client.disconnect();
  
    // 4) stop recorder & player
    try { await wavRecorderRef.current.end(); } catch {}
    try { await wavStreamPlayerRef.current.interrupt(); } catch {}
  }, []);
  
  /**
   * Handle interruption of agent speaking
   */
  const handleInterruptSpeaking = async () => {
    if (!isAgentSpeaking) return;
    
    // First, set UI state to indicate interruption
    setIsAgentSpeaking(false);
    
    // Interrupt the player
    const client = clientRef.current;
    if (client && currentResponseId) {
      try {
        // Tell the client to interrupt the conversation
        client.conversation.interrupt();
      } catch (err) {
        console.error("Failed to interrupt conversation:", err);
      }
    }
  };

  const startRecording = async () => {
    // If agent is speaking, interrupt it first
    if (isAgentSpeaking) {
      await handleInterruptSpeaking();
      // Small delay to ensure interruption completes before recording starts
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    if (isRecording) return; // Already recording
    
    try {
      // Connect if not already connected
      if (!isConnected) await connectConversation();
      
      // Set recording state
      setIsRecording(true);
      
      // Interrupt any ongoing playback
      const to = await wavStreamPlayerRef.current.interrupt();
      if (to?.trackId) await clientRef.current.cancelResponse(to.trackId, to.offset);
      
      // Start the actual recording
      wavRecorderRef.current.record(data => {
        if (clientRef.current && clientRef.current.appendInputAudio) {
          clientRef.current.appendInputAudio(data.mono);
        } else {
          console.warn("Client reference or appendInputAudio method not available");
          // Stop recording if client isn't ready
          stopRecording();
        }
      });
    } catch (err) {
      console.error("Error in startRecording:", err);
      setIsRecording(false);
    }
  };
  
  // The stopRecording function also needs improvement:
  const stopRecording = async () => {
    if (!isRecording) return;
    
    try {
      // Update UI state first
      setIsRecording(false);
      
      // Pause the recorder
      await wavRecorderRef.current.pause();
      
      // Get client reference
      const client = clientRef.current;
      
      // Make sure we have a valid client with data to send
      if (client && client.inputAudioBuffer && client.inputAudioBuffer.byteLength) {
        // Send the audio data
        client.realtime.send('input_audio_buffer.commit', {});
        client.conversation.queueInputAudio(client.inputAudioBuffer);
        client.inputAudioBuffer = new Int16Array(0);
      } else {
        console.warn("No audio data to send or client not available");
      }
    } catch (err) {
      console.error("Error in stopRecording:", err);
      // Reset state even if there's an error
      setIsRecording(false);
    }
  };
  // Handle touch start / pointer down for mic button
const handlePointerDown = async (e) => {
  // Prevent default to avoid unwanted behaviors on mobile
  e.preventDefault();
  
  // Set state for visual feedback immediately
  isLongPressingRef.current = true;
  
  // Clear any existing timers
  clearTimeout(longPressTimerRef.current);
  
  try {
    // Start recording immediately
    await startRecording();
  } catch (err) {
    console.error("Error starting recording:", err);
    // Reset state if recording fails
    isLongPressingRef.current = false;
    setIsRecording(false);
  }
};

// Handle touch end / pointer up for mic button
const handlePointerUp = async (e) => {
  // Prevent default to avoid unwanted behaviors
  e.preventDefault();
  
  // Clear any timers
  clearTimeout(longPressTimerRef.current);
  isLongPressingRef.current = false;
  
  try {
    // If we were recording, stop it
    if (isRecording) {
      await stopRecording();
    }
  } catch (err) {
    console.error("Error stopping recording:", err);
    // Reset state if stopping fails
    setIsRecording(false);
  }
};

const handlePointerLeave = async (e) => {
  e.preventDefault();
  
  if (isLongPressingRef.current) {
    isLongPressingRef.current = false;
    clearTimeout(longPressTimerRef.current);
    
    try {
      // If we were recording, stop it
      if (isRecording) {
        await stopRecording();
      }
    } catch (err) {
      console.error("Error stopping recording on pointer leave:", err);
      setIsRecording(false);
    }
  }
};
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputText.trim()) return;
  
    // If agent is speaking, interrupt it first
    if (isAgentSpeaking) {
      await handleInterruptSpeaking();
    }
  
    // Show the user's message immediately
    setMessages(prev => [
      ...prev,
      { type: 'user', content: inputText, time: new Date() }
    ]);
    setIsLoading(true);
  
    // Send text to backend only if in chat mode
    if (mode === 'chat') {
      await injectContext(inputText, true);
    } else {
      // If in voice mode, use the real-time client to handle voice-based submission
      const client = clientRef.current;
      client.sendUserMessageContent([{ type: 'input_text', text: inputText }]);
      if (!client.getTurnDetectionType()) client.createResponse();
    }
  
    // Clear the input field
    setInputText('');
  };
  
  const switchToChat = () => setMode(m => m==='voice'?'chat':'voice');

  useEffect(() => {
    const update = () => setIsDesktop(window.innerWidth >= 768);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: isDesktop ? -20 : 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: isDesktop ? -20 : 20 }}
          transition={{ duration: 0.3 }}
          className={`
            fixed z-[110] bg-white text-gray-800 flex flex-col shadow-lg
            ${isDesktop ? 
              'bottom-8 right-8 h-[500px] w-80 rounded-2xl' : 
              'top-0 right-0 left-0 h-[60vh] rounded-b-2xl'
            }
          `}
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between bg-gray-100 rounded-t-2xl">
            <div className="flex items-center gap-2">
             
                <img src={logo} alt="AI Logo" className="w-8 h-8 object-contain invert" />
            
              <h3 className="text-gray-800 font-medium">Futeur Voice</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={switchToChat}
                className="text-gray-600 hover:text-gray-800 transition-colors p-2 rounded-full hover:bg-gray-200"
                aria-label={mode === 'voice' ? 'Switch to text chat' : 'Switch to voice mode'}
              >
                {mode === 'voice' ? <MessageSquare size={20} /> : <Mic size={20} />}
              </button>
              <button
                onClick={() => {
                  disconnectConversation();
                  onClose();
                }}
                className="text-gray-600 hover:text-gray-800 transition-colors p-2 rounded-full hover:bg-gray-200"
                aria-label="Close chat"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Main Content Area */}
          <AnimatePresence mode="wait">
            {mode === 'voice' ? (
              !isConnected ? (
                <motion.div
                  key="connect-screen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex-1 flex items-center justify-center bg-white"
                >
                  <motion.button
                    onClick={connectConversation}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gray-800 text-white px-6 py-3 rounded-full font-medium flex items-center gap-2 shadow-md"
                  >
                    <Zap size={16} />
                    Start Conversation
                  </motion.button>
                </motion.div>
              ) : (
                <motion.div
                  key="voice-mode"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex-1 flex flex-col items-center justify-center relative bg-white"
                >
                  {/* Mic button with simple indicators */}
                  <div className="flex flex-col items-center justify-center mb-6">
                  <motion.button
    onPointerDown={handlePointerDown}
    onPointerUp={handlePointerUp}
    onPointerLeave={handlePointerLeave}
    onPointerCancel={handlePointerLeave}
    // Add these touch-specific handlers for better mobile support
    onTouchStart={handlePointerDown}
    onTouchEnd={handlePointerUp}
    onTouchCancel={handlePointerLeave}
    // Prevent context menu on long press
    onContextMenu={(e) => e.preventDefault()}
    // Add touch-action CSS property to disable browser handling of touches
    style={{ touchAction: 'none' }}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    className={`relative z-10 w-20 h-20 rounded-full flex items-center justify-center transition-all duration-200 shadow-md ${
      isRecording
        ? 'bg-red-50 border-2 border-red-500'
        : isAgentSpeaking
          ? 'bg-blue-50 border-2 border-blue-400'
          : 'bg-gray-50 border border-gray-300'
    }`}
    aria-label={isRecording ? "Stop recording" : "Start recording"}
  >
    {isRecording ? (
      <motion.div
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <div className="w-4 h-4 bg-red-500 rounded-sm" />
      </motion.div>
    ) : (
      <Mic size={24} color={isAgentSpeaking ? "#3b82f6" : "#374151"} strokeWidth={2} />
    )}
  </motion.button>

                    {/* Simple dot visualizer */}
                    <div className="h-8 mt-4">
                      <AnimatePresence>
                        {isRecording && (
                          <motion.div className="flex justify-center items-center gap-1 h-full">
                            {visualizerValues.map((v, i) => (
                              <motion.div
                                key={i}
                                animate={{ 
                                  height: `${v}px`,
                                  opacity: [0.5, 0.8, 0.5]
                                }}
                                transition={{ 
                                  height: { type: 'spring', stiffness: 300, damping: 10 },
                                  opacity: { duration: 1, repeat: Infinity }
                                }}
                                className="w-1 rounded-full bg-red-400"
                              />
                            ))}
                          </motion.div>
                        )}
                        {isAgentSpeaking && !isRecording && (
                          <motion.div className="flex justify-center items-center gap-1 h-full">
                            {Array(3)
                              .fill()
                              .map((_, i) => (
                                <motion.div
                                  key={i}
                                  animate={{ 
                                    opacity: [0.3, 0.8, 0.3],
                                  }}
                                  transition={{
                                    duration: 1,
                                    repeat: Infinity,
                                    delay: i * 0.3,
                                  }}
                                  className="w-2 h-2 rounded-full bg-blue-400 mx-1"
                                />
                              ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Status text */}
                    <div className="h-6 mt-2">
                      <AnimatePresence mode="wait">
                        {isLoading && !isAgentSpeaking && !isRecording ? (
                          <motion.p className="text-gray-500 text-center text-sm">
                            Processing...
                          </motion.p>
                        ) : isRecording ? (
                          <motion.p className="text-gray-800 text-center text-sm font-medium">
                            Listening...
                          </motion.p>
                        ) : isAgentSpeaking ? (
                          <motion.p className="text-gray-600 text-center text-sm">
                            Agent is speaking...
                          </motion.p>
                        ) : (
                          <motion.p className="text-gray-500 text-center text-sm">
                            Press and hold to speak
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Transcript */}
                    <AnimatePresence>
                      {audioTranscript && !isRecording && !isAgentSpeaking && (
                        <motion.div className="max-w-xs mt-4 px-4">
                          <p className="text-gray-600 text-center text-sm italic">
                            "{audioTranscript}"
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Display Image if Available */}
                    {imageUrl && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex justify-center mt-4"
                      >
                        <img src={imageUrl} alt="Response Image" className="max-w-full h-auto max-h-40 object-contain rounded-lg shadow-sm" />
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )
            ) : (
              <motion.div
                key="chat-mode"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col h-full overflow-hidden bg-white"
              >
                {/* Connect Button for Chat Mode */}
                {!isConnected && (
                  <div className="p-4 flex justify-center">
                    <motion.button
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={connectConversation}
                      className="bg-gray-800 text-white px-6 py-3 rounded-full font-medium flex items-center gap-2 shadow-md"
                    >
                      <Zap size={16} />
                      Start Conversation
                    </motion.button>
                  </div>
                )}
              
                {/* Messages List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" ref={messagesEndRef}>
                  {messages.map((message, idx) => (
                    <div
                      key={idx}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1, ease: 'easeOut' }}
                        className={`max-w-[85%] px-4 py-2 rounded-lg shadow-sm ${
                          message.type === 'user'
                            ? 'bg-blue-500 text-white rounded-tr-none'
                            : message.interrupted 
                              ? 'bg-white text-gray-800 border border-gray-200 rounded-tl-none' 
                              : 'bg-white text-gray-800 border border-gray-200 rounded-tl-none'
                        }`}
                      >
                        <p className="text-sm">{message.content}</p>
                      </motion.div>
                    </div>
                  ))}
                  
                  {/* Typing indication */}
                  {isLoading && !isAgentSpeaking && (
                    <div className="flex justify-start">
                      <motion.div 
                        className="bg-white rounded-lg p-3 shadow-sm"
                        animate={{ opacity: [0.5, 0.8, 0.5] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                      >
                        <div className="flex space-x-1">
                          {[1, 2, 3].map((dot) => (
                            <motion.div
                              key={dot}
                              className="w-2 h-2 bg-gray-400 rounded-full"
                              animate={{ y: [0, -3, 0] }}
                              transition={{ 
                                repeat: Infinity, 
                                duration: 1,
                                delay: dot * 0.15
                              }}
                            />
                          ))}
                        </div>
                      </motion.div>
                    </div>
                  )}
                </div>
              
                {/* Input Area */}
                <div className="p-3 border-t border-gray-200 bg-white">
                  <form onSubmit={handleSubmit} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={inputText}
                      onChange={e => setInputText(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 text-gray-800 bg-gray-100 px-4 py-2 rounded-full focus:outline-none focus:ring-1 focus:ring-gray-400 placeholder-gray-400"
                    />
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="submit"
                      disabled={!inputText.trim() || isLoading || !isConnected}
                      className={`p-2 rounded-full ${
                        inputText.trim() && isConnected && !isLoading
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <Send size={18} />
                    </motion.button>
                  </form>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Simple Footer */}
          <div className="py-2 px-4 bg-white border-t border-gray-200 flex justify-between items-center text-xs text-gray-500 rounded-b-2xl">
            {isConnected && (
              <div className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${isAgentSpeaking ? 'bg-blue-500' : 'bg-green-500'}`} />
                <span>{isAgentSpeaking ? 'Speaking' : 'Connected'}</span>
              </div>
            )}
            <span className="ml-auto">Powered by Futeur AI</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default MobileChatInterface;